import React, { useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Button,
  Paper,
  IconButton
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import {
  Search as SearchIcon,
  RefreshCw,
  TrendingUp,
  ExternalLink
} from 'lucide-react'

import searchService from '../../services/AGTUps-product-services/searchService'

// ---------------------------------------------------------------------------
// Colores
// ---------------------------------------------------------------------------
const navigationColors = (theme) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(40, 40, 42, 0.94)'
        : 'rgba(242, 242, 247, 1)',
    textDefault: theme.palette.mode === 'dark' ? '#FFFFFF' : '#666666',
    textPlaceholder:
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#666666',
    iconColor: isWallpaperMode ? '#FFFFFF' : '#007BFE'
  };
}

// ---------------------------------------------------------------------------
// Contenedor principal con scroll interno
// ---------------------------------------------------------------------------
const StyledContainer = styled(Box)(() => ({
  backgroundColor: 'transparent',
  padding: '24px',
  width: '100%',
  height: 'calc(100vh - 200px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  }
}))

// ---------------------------------------------------------------------------
// SearchBar con estilos unificados
// ---------------------------------------------------------------------------
const StyledSearchBar = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : navigationColors(theme).background,
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      '& fieldset': { border: 'none' },
      '& input': {
        color: navigationColors(theme).textDefault,
        '&::placeholder': {
          color: navigationColors(theme).textPlaceholder,
          opacity: 1
        }
      }
    }
  };
})

// ---------------------------------------------------------------------------
// Botón de acciones
// ---------------------------------------------------------------------------
const StyledActionButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? '#FFFFFF' : navigationColors(theme).iconColor,
    borderRadius: '30px',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    textTransform: 'none',
    padding: '6px 16px',
    fontWeight: 500,
    // Sin sombra ni transiciones
    boxShadow: 'none',
    transition: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#005FDB'
    }
  };
})

// ---------------------------------------------------------------------------
// Tarjeta para cada resultado
// ---------------------------------------------------------------------------
const StyledResultCard = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '16px',
    padding: '16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : navigationColors(theme).background,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: navigationColors(theme).textDefault,
    boxShadow: 'none',
    marginBottom: '16px'
  };
})

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const Search = () => {
  const theme = useTheme()

  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState([])
  const [hasSearched, setHasSearched] = useState(false)

  // Para simular "tendencias" o "últimas búsquedas"
  const trendingSearches = [
    { label: 'Proveedores de Acero', icon: <TrendingUp size={16} /> },
    { label: 'Paneles Solares', icon: <TrendingUp size={16} /> },
    { label: 'Maquinaria Pesada', icon: <TrendingUp size={16} /> }
  ]

  // Lógica para realizar la búsqueda
  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setLoading(true)
    try {
      const response = await searchService.createSearch(searchQuery)
      if (response.success) {
        // Asumimos que en response.data vienen { results, suggestions... }
        setResults(response.data.results || [])
      }
      setHasSearched(true)
    } catch (error) {
      console.error('[Search] handleSearch error:', error)
    } finally {
      setLoading(false)
    }
  }

  // Opción de refrescar el estado actual
  const handleRefresh = () => {
    setSearchQuery('')
    setResults([])
    setHasSearched(false)
  }

  // -------------------------------------------------------------------------
  // 1. PANTALLA "LANDING"
  // -------------------------------------------------------------------------
  if (!hasSearched) {
    return (
      <StyledContainer>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            pt: 2
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              [theme.breakpoints.down('sm')]: {
                fontSize: '1.75rem'
              }
            }}
          >
            ¿Qué proveedor buscamos hoy?
          </Typography>

          {/* Campo de búsqueda */}
          <Box sx={{ width: '100%', maxWidth: 600, px: 2 }}>
            <StyledSearchBar
              fullWidth
              placeholder="Busca proveedores, productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </Box>

          {/* Botón para buscar con spinner */}
          <Box>
            <StyledActionButton onClick={handleSearch} disabled={loading}>
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SearchIcon size={16} />
              )}
              <span style={{ marginLeft: 8 }}>
                {loading ? 'Buscando...' : 'Buscar'}
              </span>
            </StyledActionButton>
          </Box>

          {/* Tendencias */}
          <Box
            sx={{
              mt: 2,
              textAlign: 'center',
              px: 2,
              opacity: loading ? 0.3 : 1,
              transition: 'opacity 0.5s ease-in-out'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 500,
                [theme.breakpoints.down('sm')]: {
                  fontSize: '1.1rem'
                }
              }}
            >
              Tendencias de búsqueda
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'center'
              }}
            >
              {trendingSearches.map((trend, idx) => (
                <Button
                  key={idx}
                  variant="outlined"
                  onClick={() => {
                    setSearchQuery(trend.label)
                    handleSearch()
                  }}
                  startIcon={trend.icon}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    borderColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
                    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
                    '&:hover': {
                      borderColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
                      backgroundColor: theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.1)' : undefined
                    },
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '0.9rem',
                      padding: '4px 8px'
                    }
                  }}
                >
                  {trend.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      </StyledContainer>
    )
  }

  // -------------------------------------------------------------------------
  // 2. PANTALLA "RESULTADOS"
  // -------------------------------------------------------------------------
  return (
    <StyledContainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            [theme.breakpoints.down('sm')]: {
              fontSize: '1.3rem'
            }
          }}
        >
          Resultados de Búsqueda
        </Typography>

        {/* Barra de búsqueda en la página de resultados */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <StyledSearchBar
              fullWidth
              placeholder="Busca proveedores, productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </Grid>

          <Grid item>
            <StyledActionButton
              onClick={handleSearch}
              disabled={loading}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  padding: '6px 12px'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SearchIcon size={16} />
              )}
              <span style={{ marginLeft: 8 }}>
                {loading ? 'Buscando...' : 'Buscar'}
              </span>
            </StyledActionButton>
          </Grid>

          <Grid item>
            <IconButton
              onClick={handleRefresh}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  padding: '6px'
                }
              }}
            >
              {loading ? <CircularProgress size={20} /> : <RefreshCw size={20} />}
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Sin resultados */}
      {!loading && results.length === 0 && (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          No hay resultados. Intenta con otra búsqueda.
        </Typography>
      )}

      {/* Resultados */}
      {!loading &&
        results.map((item, idx) => (
          <StyledResultCard key={idx}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {item.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              {item.snippet}
            </Typography>

            {/* AGT dice: (si existe relevanceReason) */}
            {item.relevanceReason && (
              <Box
                sx={{
                  mt: 2,
                  borderLeft: theme.palette.actualMode === 'wallpaper'
                    ? '4px solid #FFFFFF'
                    : `4px solid ${navigationColors(theme).iconColor}`,
                  paddingLeft: 1
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontStyle: 'italic',
                    color: navigationColors(theme).textPlaceholder
                  }}
                >
                  <strong>AGT dice:</strong> {item.relevanceReason}
                </Typography>
              </Box>
            )}

            <Box sx={{ textAlign: 'right', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => window.open(item.link, '_blank')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : navigationColors(theme).iconColor,
                  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : navigationColors(theme).iconColor,
                  // sin sombras ni transiciones
                  boxShadow: 'none',
                  transition: 'none',
                  '&:hover': {
                    borderColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#005FDB',
                    backgroundColor: theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
                  }
                }}
                startIcon={<ExternalLink size={16} />}
              >
                Visitar
              </Button>
            </Box>
          </StyledResultCard>
        ))}
    </StyledContainer>
  )
}

export default Search
